import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, LoaderCircle } from "lucide-react";
import { contractApi, formatEuro, publicContractError } from "../lib/contractApi";
import { formatContractDate } from "../lib/contractUser";
import { PublicShell } from "./PublicContract";

export default function TermsChange() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    contractApi.getChange(token).then(({ data }) => setContract(data)).catch((e) => setError(publicContractError(e)));
  }, [token]);

  const submit = async () => {
    if (!accepted || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await contractApi.acceptChange(token);
      setCompleted(data);
    } catch (e) {
      setError(publicContractError(e));
    } finally { setBusy(false); }
  };

  if (error && !contract) return <PublicShell><State icon={AlertTriangle} title="Änderung nicht verfügbar">{error}</State></PublicShell>;
  if (!contract) return <PublicShell><div className="flex min-h-[40vh] items-center justify-center gap-2 text-slate-600"><LoaderCircle className="h-5 w-5 animate-spin" /> Änderung wird geladen …</div></PublicShell>;
  if (completed) return <PublicShell><State icon={CheckCircle2} title="Änderung bestätigt">Die neuen Bedingungen gelten erst ab {formatContractDate(completed.future_terms?.effective_from)}. Der bereits bezahlte Zeitraum bleibt unverändert.</State></PublicShell>;

  const future = contract.pending_change?.terms || {};
  const current = contract.terms || {};
  const legal = contract.pending_change?.legal_snapshots || {};
  return <PublicShell><main className="mx-auto max-w-3xl space-y-6">
    <header><p className="text-sm font-semibold uppercase tracking-wider text-red-700">Künftige Vertragsänderung</p><h1 className="mt-2 text-3xl font-semibold">Neue Bedingungen prüfen</h1><p className="mt-3 leading-7 text-slate-600">Der aktuelle, bereits bezahlte Nutzungszeitraum bleibt unverändert. Deine Zustimmung gilt nur für den nächsten Zeitraum ab {formatContractDate(contract.pending_change?.effective_from)}.</p></header>
    <section className="grid gap-4 sm:grid-cols-2">
      <Terms title="Bisher" terms={current} />
      <Terms title="Künftig" terms={future} emphasized />
    </section>
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xl font-semibold">Neue Vertragsfassung</h2>
      <div className="contract-legal mt-4 rounded-xl bg-slate-50 p-5" dangerouslySetInnerHTML={{ __html: legal.usage_contract?.html || "" }} />
      <label className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm leading-6"><input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-4 w-4 accent-red-700" /><span>Ich stimme den dargestellten neuen Bedingungen ausdrücklich für den nächsten Nutzungszeitraum zu.</span></label>
      {error && <div role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      <button type="button" disabled={!accepted || busy} onClick={submit} className="mt-5 w-full rounded-xl bg-red-700 px-4 py-3 font-semibold text-white disabled:bg-slate-300">{busy ? "Wird bestätigt …" : "Neue Bedingungen bestätigen"}</button>
      <p className="mt-3 text-center text-xs leading-5 text-slate-500">Ohne Zustimmung entsteht keine Zahlungspflicht zu den neuen Bedingungen.</p>
    </section>
  </main></PublicShell>;
}

const Terms = ({ title, terms, emphasized = false }) => <section className={`rounded-2xl border p-5 ${emphasized ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"}`}><h2 className="font-semibold">{title}</h2><dl className="mt-4 space-y-3 text-sm"><div><dt className="text-slate-500">Gesamtpreis</dt><dd className="font-semibold">{formatEuro(terms.price_cents)}</dd></div><div><dt className="text-slate-500">Zeitraum</dt><dd className="font-semibold">{terms.duration_months} Monate</dd></div></dl></section>;
const State = ({ icon: Icon, title, children }) => <div className="mx-auto max-w-xl rounded-2xl bg-white p-8 text-center shadow-sm"><Icon className="mx-auto h-10 w-10 text-red-700" /><h1 className="mt-4 text-2xl font-semibold">{title}</h1><p className="mt-3 text-slate-600">{children}</p></div>;
